// root '../../../';

// dependencies
import { Child } from 'pym.js';
import * as d3 from 'd3';
import { extent, max, min } from 'd3-array';
import Zion from './utils/Zion';
import { linearAxis, timeAxis } from '../../../factory/axes';

// polyfills
// import fetch from 'unfetch';
import './utils/polyfills'; // (includes Array.findIndex && Array.find)

// utils & graphic
import { stylePremium } from './utils/helpers';
import config from './config';
import graphic from './graphic';

// styles
import './styles/graphic.scss';

/* develblock:start */
import meta from './data/meta.json';

const pkg = require('../package.json');

if (process.env.NODE_ENV === 'development') {
  console.clear();
}

d3.timeFormatDefaultLocale({
  periods: ['a.m.', 'p.m.'],
  days: [
    'Sunday',
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday',
  ],
  shortDays: ['Sun', 'Mon', 'Tues', 'Wed', 'Thurs', 'Fri', 'Sat'],
  months: [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ],
  shortMonths: [
    'Jan.',
    'Feb.',
    'March',
    'April',
    'May',
    'June',
    'July',
    'Aug.',
    'Sept.',
    'Oct.',
    'Nov.',
    'Dec.',
  ],
});

let chartData;
let rawData;

const margins = {
  top: 40,
  right: 95,
  bottom: 25,
  left: 35,
};

const parser = d3.timeParse('%Y-%m-%d');

// let recessionData;
let minValue;
let maxValue;

let minDomain;
let maxDomain;

let lastData;
let lastUpdatedStr;
let latestMonth;

// eslint-disable-next-line  no-use-before-define
window.pymChild = new Child({ id: pkg.name });

window.onload = () => {
  // necessary evil for right now

  if (process.env.NODE_ENV === 'development') {
    Object.keys(meta).forEach((id) => {
      const node = document.querySelector(`.${id}`);

      if (node) {
        node.innerHTML = meta[id];
      }
    });
  }

  const { pymChild } = window;
  pymChild.sendHeight();

  // If embedded, send Zion page view event
  if (window.pymChild.parentUrl) {
    const zion = new Zion(pkg.name, pymChild.parentUrl.split('?')[0]);
  }
};

const CNNPolitics = false;
const CNNBusiness = false;
const pTime = d3.timeParse('%Y-%m-%d');

stylePremium(CNNPolitics, CNNBusiness);

const rParser = d3.timeParse('%Y-%m-%d');

// Single-date event markers drawn as vertical lines. Labels are right-anchored
// (extending left of the line) and placed near the top, in the empty space
// above the SPR line in recent years. Use \n for multi-line labels and the
// per-event offsets to fine-tune placement.
const eventData = [
  {
    date: '2022-02-24', label: 'Russia invades\nUkraine', anchor: 'end', labelXOffset: -6, labelYOffset: 0,
  },
];

let dekText;
let sourceText;

d3.json('https://ix.cnn.io/data/strategic-petroleum-reserve/latest.json').then((data) => {
  // Map and filter data, removing any invalid entries
  chartData = data.data
    .map(d => ({
      date: d3.timeParse('%Y-%m-%d')(d.date),
      value: +d.value,
      source: d.source,
    }))
    .filter(d => d.date && !isNaN(d.value) && d.value !== null);

  // Validate we have data to display
  if (chartData.length === 0) {
    console.error('No valid strategic petroleum reserve data available');
    d3.select('#graphic').append('p')
      .style('text-align', 'center')
      .style('padding', '40px')
      .text('Data temporarily unavailable');
    return;
  }

  minDomain = d3.min(chartData, d => d.date);
  maxDomain = d3.max(chartData, d => d.date);

  minValue = 0;
  // Values are in thousand barrels; round the top of the scale up to a clean
  // 100M-barrel (100,000 thousand-barrel) increment for tidy gridlines.
  maxValue = Math.ceil(d3.max(chartData, d => d.value) / 100000) * 100000;

  // Get the last data point
  const lastDataArray = chartData.filter(d => d.date.getTime() === maxDomain.getTime());
  lastData = lastDataArray.length > 0 ? lastDataArray[0] : chartData[chartData.length - 1];

  lastUpdatedStr = data.lastUpdatedStr;
  latestMonth = d3.timeFormat('%B %Y')(lastData.date);

  dekText = data.summaryHtml;

  if (dekText && dekText !== '') {
    d3.select('h2.dek')
      .html(dekText);
  }

  // The historical series is EIA weekly data; the most recent point can come
  // from the DOE daily report (updates Mondays) ahead of EIA (Wednesdays).
  // Reflect whichever source supplied the latest value.
  const latestSource = (data.latestSource || lastData.source || '').toLowerCase();
  if (latestSource.indexOf('doe') !== -1) {
    sourceText = 'US Department of Energy, Office of Petroleum Reserves; US Energy Information Administration';
  } else {
    sourceText = 'US Energy Information Administration';
  }

  d3.select('#footer .source')
    .html(`Source: ${sourceText}`);

  draw();
  window.onresize = draw;
});


function draw() {
  const container = d3.select('#graphic');
  container.html('');

  const initialWidth = container.node().clientWidth;
  const isMobile = initialWidth <= 500;
  const height = 400;

  const chartHeight = height - margins.top - margins.bottom;
  const chartWidth = initialWidth - margins.left - margins.right;

  const xTicks = isMobile ? 2 : 5;
  const yTicks = isMobile ? 5 : 6;

  const xScale = d3.scaleTime()
    .domain([minDomain, maxDomain])
    .range([0, chartWidth]);

  const yScale = d3.scaleLinear()
    .domain([minValue, maxValue])
    .range([chartHeight, 0]);

  const xAxis = d3.axisBottom()
    .scale(xScale)
    .ticks(xTicks)
    .tickFormat((d, i) => {
      if (i === 0 || d3.timeFormat('%m')(d) == '01') {
        return d3.timeFormat('%Y')(d);
      }
      return d3.timeFormat('%b')(d);
    });

  const yAxis = d3.axisLeft()
    .scale(yScale)
    .ticks(yTicks)
    .tickSize(-chartWidth)
    .tickFormat((d, i, x) => {
      // Source values are in thousand barrels; show millions of barrels.
      const millions = d / 1000;
      return i == x.length - 1 ? `${d3.format('.0f')(millions)}M` : `${d3.format('.0f')(millions)}`;
    });

  const path = d3.line()
    .curve(d3.curveLinear)
    .x(d => xScale(d.date))
    .y(d => yScale(d.value));

  const svg = container.append('svg')
    .attr('width', initialWidth)
    .attr('height', height);

  const g = svg.append('g')
    .attr('transform', `translate(${[margins.left, margins.top]})`);

  g.append('g')
    .attr('class', 'grid x')
    .attr('transform', `translate(${[0, chartHeight]})`)
    .call(xAxis);

  g.append('g')
    .attr('class', 'grid y')
    .call(yAxis);

  const line = g.append('g')
    .attr('class', 'line');

  const lineWidth = window.innerWidth < 768 ? 1.5 : 2;

  line.append('path')
    .datum(chartData)
    .attr('stroke', d => '#7c4ea5')
    .attr('stroke-width', lineWidth)
    .attr('fill', 'none')
    .attr('d', path);

  d3.select('.grid.y .domain').remove();

  // Add annotation for Russia invades Ukraine (Feb 24, 2022)
  const russiaUkraineDate = new Date(2022, 1, 24); // February 24, 2022

  // Only show annotation if date is within chart range
  if (russiaUkraineDate >= minDomain && russiaUkraineDate <= maxDomain) {
    const russiaAnnotation = g.append('g')
      .attr('class', 'russia-annotation');

    // Vertical dotted line
    russiaAnnotation.append('line')
      .attr('x1', xScale(russiaUkraineDate))
      .attr('x2', xScale(russiaUkraineDate))
      .attr('y1', 0)
      .attr('y2', chartHeight)
      .attr('stroke', '#666')
      .attr('stroke-width', 1)
      .attr('stroke-dasharray', '4,4');

    // Date label (bold) - positioned at top like original design.
    // Nudge the whole label group up/left with these offsets.
    const russiaXOffset = -8;
    const russiaYOffset = -10;
    const labelX = xScale(russiaUkraineDate) + russiaXOffset;
    const dateY = 0 + russiaYOffset;
    const line1Y = 15 + russiaYOffset;
    const line2Y = 30 + russiaYOffset;

    // Date halo
    russiaAnnotation.append('text')
      .attr('x', labelX)
      .attr('y', dateY)
      .attr('text-anchor', 'end')
      .attr('class', 'russia-label-date-halo')
      .style('font-size', '12px')
      .style('font-weight', 'bold')
      .style('stroke', '#fff')
      .style('stroke-width', '3px')
      .style('fill', 'none')
      .text('Feb. 24, 2022');

    // Date text
    russiaAnnotation.append('text')
      .attr('x', labelX)
      .attr('y', dateY)
      .attr('text-anchor', 'end')
      .attr('class', 'russia-label-date')
      .style('font-size', '12px')
      .style('font-weight', 'bold')
      .style('fill', '#333')
      .text('Feb. 24, 2022');

    // First line halo
    russiaAnnotation.append('text')
      .attr('x', labelX)
      .attr('y', line1Y)
      .attr('text-anchor', 'end')
      .attr('class', 'russia-label-text-halo')
      .style('font-size', '12px')
      .style('stroke', '#fff')
      .style('stroke-width', '3px')
      .style('fill', 'none')
      .text('Russia invades');

    // First line text
    russiaAnnotation.append('text')
      .attr('x', labelX)
      .attr('y', line1Y)
      .attr('text-anchor', 'end')
      .attr('class', 'russia-label-text')
      .style('font-size', '12px')
      .style('fill', '#666')
      .text('Russia invades');

    // Second line halo
    russiaAnnotation.append('text')
      .attr('x', labelX)
      .attr('y', line2Y)
      .attr('text-anchor', 'end')
      .attr('class', 'russia-label-text-halo')
      .style('font-size', '12px')
      .style('stroke', '#fff')
      .style('stroke-width', '3px')
      .style('fill', 'none')
      .text('Ukraine');

    // Second line text
    russiaAnnotation.append('text')
      .attr('x', labelX)
      .attr('y', line2Y)
      .attr('text-anchor', 'end')
      .attr('class', 'russia-label-text')
      .style('font-size', '12px')
      .style('fill', '#666')
      .text('Ukraine');
  }

  // Add annotation for US/Iran war start date (Feb 28, 2026)
  const warStartDate = new Date(2026, 1, 28); // February 28, 2026

  // Only show annotation if date is within chart range
  if (warStartDate >= minDomain && warStartDate <= maxDomain) {
    const warAnnotation = g.append('g')
      .attr('class', 'war-annotation');

    // Vertical dotted line
    warAnnotation.append('line')
      .attr('x1', xScale(warStartDate))
      .attr('x2', xScale(warStartDate))
      .attr('y1', 0)
      .attr('y2', chartHeight)
      .attr('stroke', '#666')
      .attr('stroke-width', 1)
      .attr('stroke-dasharray', '4,4');

    // Date label (bold) - positioned to right of line
    const labelX = xScale(warStartDate) + 8;

    // Date halo
    warAnnotation.append('text')
      .attr('x', labelX)
      .attr('y', 15)
      .attr('text-anchor', 'start')
      .attr('class', 'war-label-date-halo')
      .style('font-size', '12px')
      .style('font-weight', 'bold')
      .style('stroke', '#fff')
      .style('stroke-width', '3px')
      .style('fill', 'none')
      .text('Feb. 28, 2026');

    // Date text
    warAnnotation.append('text')
      .attr('x', labelX)
      .attr('y', 15)
      .attr('text-anchor', 'start')
      .attr('class', 'war-label-date')
      .style('font-size', '12px')
      .style('font-weight', 'bold')
      .style('fill', '#333')
      .text('Feb. 28, 2026');

    // First line halo
    warAnnotation.append('text')
      .attr('x', labelX)
      .attr('y', 30)
      .attr('text-anchor', 'start')
      .attr('class', 'war-label-text-halo')
      .style('font-size', '12px')
      .style('stroke', '#fff')
      .style('stroke-width', '3px')
      .style('fill', 'none')
      .text('US and Israel');

    // First line text
    warAnnotation.append('text')
      .attr('x', labelX)
      .attr('y', 30)
      .attr('text-anchor', 'start')
      .attr('class', 'war-label-text')
      .style('font-size', '12px')
      .style('fill', '#666')
      .text('US and Israel');

    // Second line halo
    warAnnotation.append('text')
      .attr('x', labelX)
      .attr('y', 45)
      .attr('text-anchor', 'start')
      .attr('class', 'war-label-text-halo')
      .style('font-size', '12px')
      .style('stroke', '#fff')
      .style('stroke-width', '3px')
      .style('fill', 'none')
      .text('initial strikes');

    // Second line text
    warAnnotation.append('text')
      .attr('x', labelX)
      .attr('y', 45)
      .attr('text-anchor', 'start')
      .attr('class', 'war-label-text')
      .style('font-size', '12px')
      .style('fill', '#666')
      .text('initial strikes');

    // Third line halo
    warAnnotation.append('text')
      .attr('x', labelX)
      .attr('y', 60)
      .attr('text-anchor', 'start')
      .attr('class', 'war-label-text-halo')
      .style('font-size', '12px')
      .style('stroke', '#fff')
      .style('stroke-width', '3px')
      .style('fill', 'none')
      .text('on Iran');

    // Third line text
    warAnnotation.append('text')
      .attr('x', labelX)
      .attr('y', 60)
      .attr('text-anchor', 'start')
      .attr('class', 'war-label-text')
      .style('font-size', '12px')
      .style('fill', '#666')
      .text('on Iran');
  }

  // Only add annotation if we have valid last data point
  if (lastData && lastData.date && !isNaN(lastData.value)) {
    const annotation = g.append('g')
      .attr('class', 'annotation')
      .attr('transform', `translate(${[xScale(lastData.date), yScale(lastData.value)]})`);

    annotation.append('circle')
      .attr('r', '3')
      .attr('fill', '#7c4ea5')
      .attr('stroke', 'none');

    // Date halo
    annotation.append('text')
      .attr('dx', '10px')
      .attr('class', 'annotation-date-halo')
      .style('font-size', '12px')
      .style('font-weight', 'bold')
      .style('stroke', '#fff')
      .style('stroke-width', '3px')
      .style('fill', 'none')
      .text(d3.timeFormat('%b %-d, %Y')(lastData.date));

    // Date text
    annotation.append('text')
      .attr('dx', '10px')
      .attr('class', 'annotation-date')
      .style('font-size', '12px')
      .style('font-weight', 'bold')
      .text(d3.timeFormat('%b %-d, %Y')(lastData.date));

    // Value halo
    annotation.append('text')
      .attr('dx', '10px')
      .attr('dy', '1.2em')
      .attr('class', 'annotation-value-halo')
      .style('font-size', '12px')
      .style('stroke', '#fff')
      .style('stroke-width', '3px')
      .style('fill', 'none')
      .text(`${d3.format(',.1f')(lastData.value / 1000)}M`);

    // Value text
    annotation.append('text')
      .attr('dx', '10px')
      .attr('dy', '1.2em')
      .attr('class', 'annotation-value')
      .style('font-size', '12px')
      .text(`${d3.format(',.1f')(lastData.value / 1000)}M`);
  }

  // Append the current date as a timestamp below the chart
  const currentDateString = `Note: Data last updated ${lastUpdatedStr}.`;

  const timestamp = container.append('div')
    .attr('class', 'footnote')
    .text(currentDateString);

  window.pymChild.sendHeight();
}
